import { useEffect, useRef, useState } from 'react';

// No allow-scripts: the mail can't run code, and same-origin only lets the pane read its height
const FRAME_SANDBOX = 'allow-same-origin allow-popups allow-popups-to-escape-sandbox';
// Appended last: a mail's html{height:100%} would otherwise pin the frame to its own viewport
const AUTO_HEIGHT = ':root,:root body{height:auto!important;min-height:0!important}';

const defaultsFor = (frame: HTMLIFrameElement) => {
  const { fontFamily, fontSize, lineHeight, color } = getComputedStyle(frame);
  // Sized images keep their size: a 560x1 spacer must stay 1px tall
  return `body{margin:0;font-family:${fontFamily};font-size:${fontSize};line-height:${lineHeight};color:${color};overflow-wrap:anywhere}img:not([width]):not([height]){max-width:100%;height:auto}`;
};

export function EmailHtmlFrame({ html, title }: { html: string; title: string }) {
  const observerRef = useRef<ResizeObserver>(null);
  const [height, setHeight] = useState<number>();
  useEffect(() => () => observerRef.current?.disconnect(), []);

  const fit = (frame: HTMLIFrameElement) => {
    const document = frame.contentDocument;
    if (!document) return;
    const defaults = document.createElement('style');
    defaults.textContent = defaultsFor(frame);
    // Prepended so the mail's own styles still override these defaults
    document.head.prepend(defaults);
    const autoHeight = document.createElement('style');
    autoHeight.textContent = AUTO_HEIGHT;
    document.body.append(autoHeight);
    observerRef.current?.disconnect();
    const root = document.documentElement;
    observerRef.current = new ResizeObserver(() =>
      setHeight(Math.ceil(root.getBoundingClientRect().height)),
    );
    observerRef.current.observe(root);
  };

  return (
    <iframe
      title={title}
      onLoad={event => fit(event.currentTarget)}
      sandbox={FRAME_SANDBOX}
      srcDoc={html}
      className="block w-full border-0 text-sm"
      style={height === undefined ? undefined : { height }}
      data-testid="email-body"
      data-status="stored"
    />
  );
}
